import React, { ReactNode, useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import OptimizedImage from '@/components/common/OptimizedImage';
import { Info } from 'lucide-react';

export interface BadgeProps {
  icon?: ReactNode;
  text: string;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  color?: string;
  className?: string;
}

export interface BusinessHeroProps {
  title: string;
  titleHighlight?: string;
  subtitle?: string;
  description?: string;
  details?: string;
  primaryCTA?: {
    label: string;
    href: string;
    icon?: ReactNode;
  };
  secondaryCTA?: {
    label: string;
    href: string;
    icon?: ReactNode;
  };
  image?: {
    src: string;
    alt: string;
    width?: number;
    height?: number;
  };
  badges?: BadgeProps[];
  imageInfo?: {
    text: string;
    link?: string;
  };
  backgroundStyle?: 'blue' | 'orange' | 'green' | 'purple' | 'silver' | 'default';
  backgroundClassName?: string;
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  detailsClassName?: string;
  children?: ReactNode;
}

const BusinessHero = ({
  title,
  titleHighlight,
  subtitle,
  description,
  details,
  primaryCTA,
  secondaryCTA,
  image,
  badges,
  imageInfo,
  backgroundStyle = 'default',
  backgroundClassName,
  className,
  titleClassName,
  descriptionClassName,
  detailsClassName,
  children,
}: BusinessHeroProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipRef = useRef(null);
  
  // Schließe Tooltip wenn außerhalb geklickt wird
  useEffect(() => {
    if (!imageInfo) return;
    
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setTooltipOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tooltipRef, imageInfo]);
  
  // Vordefinierte Farbverläufe für verschiedene Seiten
  const backgroundStyles = {
    default: "bg-gradient-to-br from-blue-800/5 to-blue-900/10",
    blue: "bg-gradient-to-r from-blue-50 to-indigo-100",
    orange: "bg-gradient-to-br from-orange-50 to-amber-100",
    green: "bg-gradient-to-br from-emerald-50 to-green-100",
    purple: "bg-gradient-to-br from-purple-50 to-indigo-100",
    silver: "bg-gradient-to-br from-gray-50 to-slate-100"
  };
  
  // Verwende den benutzerdefinierten Hintergrund, wenn angegeben, ansonsten den vordefinierten Stil
  const finalBackgroundClassName = backgroundClassName || backgroundStyles[backgroundStyle];
  
  // Finaler Titel mit optionalem Highlight
  const finalTitle = titleHighlight 
    ? title.replace(titleHighlight, `<span class="text-blue-700">${titleHighlight}</span>`)
    : title;
    
  // Badge-Positionierungsklassen
  const badgePositions = {
    'top-left': 'top-[5%] left-[5%]',
    'top-right': 'top-[5%] right-[5%]',
    'bottom-left': 'bottom-[5%] left-[5%]',
    'bottom-right': 'bottom-[5%] right-[5%]'
  };

  return (
    <section className={cn("pt-32 pb-16 md:pt-24 md:pb-24 overflow-hidden", finalBackgroundClassName)}>
      <div className="container">
        <div className={cn("flex flex-col lg:flex-row gap-12 items-center", className)}>
          <div className="lg:w-1/2">
            <h1 
              className={cn("text-3xl md:text-4xl font-bold mb-6 leading-tight", titleClassName)}
              dangerouslySetInnerHTML={{ __html: finalTitle }}
            />
            
            {subtitle && (
              <p className="text-xl mb-6 text-gray-700">
                {subtitle}
              </p>
            )}
            
            {description && (
              <p className={cn("text-lg text-gray-700 mb-6", descriptionClassName)}>
                {description}
              </p>
            )}
            
            {details && (
              <p className={cn("text-gray-600 mb-8", detailsClassName)}>
                {details}
              </p>
            )}
            
            {(primaryCTA || secondaryCTA) && (
              <div className="flex flex-col sm:flex-row gap-4">
                {primaryCTA && (
                  <Button 
                    size="lg" 
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 shadow-md hover:shadow-lg transition-all duration-300"
                    asChild
                  >
                    <a href={primaryCTA.href}>
                      {primaryCTA.label}
                      {primaryCTA.icon && (
                        <span className="ml-2">{primaryCTA.icon}</span>
                      )}
                    </a>
                  </Button>
                )}
                
                {secondaryCTA && (
                  <Button 
                    variant="outline" 
                    size="lg"
                    className="border-blue-700 text-blue-700 hover:bg-blue-50 transition-all duration-300"
                    asChild
                  >
                    <a href={secondaryCTA.href} className="flex items-center">
                      {secondaryCTA.label}
                      {secondaryCTA.icon && (
                        <span className="ml-2">{secondaryCTA.icon}</span>
                      )}
                    </a>
                  </Button>
                )}
              </div>
            )}
            
            {children}
          </div>
          
          {image && (
            <div className="lg:w-1/2 flex justify-center">
              <div className="relative bg-white p-2 rounded-xl shadow-xl hover:shadow-blue-200/30 transition-all duration-300">
                <OptimizedImage
                  src={image.src}
                  alt={image.alt}
                  width={image.width || 600}
                  height={image.height || 400}
                  className="rounded-lg"
                  loading="eager"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                />
                
                {/* Badges */}
                {badges && badges.map((badge, index) => (
                  <div 
                    key={index} 
                    className={cn(
                      "absolute bg-white rounded-lg shadow-lg p-3 transform z-10",
                      index % 2 === 0 ? "rotate-3" : "-rotate-3",
                      badgePositions[badge.position],
                      badge.className
                    )}
                  >
                    <div className={cn("flex items-center", 
                      badge.color ? `text-${badge.color}-600` : "text-blue-600"
                    )}>
                      {badge.icon}
                      <span className="text-sm font-bold">{badge.text}</span>
                    </div>
                  </div>
                ))}
                
                {/* Info Tooltip */}
                {imageInfo && (
                  <div className="absolute bottom-4 right-4" ref={tooltipRef}>
                    <Info 
                      className="h-4 w-4 text-white opacity-50 hover:opacity-100 cursor-pointer" 
                      onClick={() => setTooltipOpen(!tooltipOpen)}
                    />
                    {tooltipOpen && (
                      <div className="absolute bottom-full right-0 mb-2 bg-black/80 text-white text-[10px] rounded px-2 py-1 whitespace-nowrap z-10">
                        {imageInfo.link ? (
                          <a 
                            href={imageInfo.link}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {imageInfo.text}
                          </a>
                        ) : (
                          imageInfo.text
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export { BusinessHero };
export default BusinessHero; 